/**
 * Approvals module -- signal-based state management (v2)
 *
 * Mirrors the old app.js approval logic with Preact signals.
 * Handles loading, filtering, batch operations, and real-time WS updates.
 * v2: Added 'sms' and 'email' filter categories alongside existing ones.
 */

import { openedApproval } from '@/core/api/approval-origin';
import { signal, computed } from '@preact/signals';
import { api } from '@/core/api/client';
import { onMessage } from '@/core/ws/client';
import { pendingCount as tabBarPendingCount } from '../../shell/TabBar';
import type {
  Approval,
  ApprovalShownFields,
  WSMessage,
  WSNewApproval,
  WSApprovalUpdate,
} from '@/core/api/types';

// ---- Signals ----

export const approvals = signal<Approval[]>([]);
export const approvalView = signal<'pending' | 'messages'>('pending');
export const activeCategory = signal<string>('all');
export const currentApprovalId = signal<string | null>(null);
/** A card opened to be declined (row swipe left): its detail opens the decline sheet once (PH-06a). */
export const declineRequestedId = signal<string | null>(null);
export const batchSelected = signal<Set<string>>(new Set());
export const loading = signal(false);
export const error = signal<string | null>(null);

// ---- Computed ----

export const batchMode = computed(() => batchSelected.value.size > 0);

/**
 * The ticked cards that may still go together. A card that changed after it was ticked (gained an attachment,
 * became a group text or an action, left the queue) drops out here; Select mode sends only these (PH-06b checker 1).
 */
export const batchRows = computed(() =>
  // canBatch, the same strict rule the checkbox uses: a known, allowed line and nothing that needs its own look.
  approvals.value.filter((a) => batchSelected.value.has(a.id) && canBatch(a)),
);

/** The batch review sheet is open: its own buttons decide, and no card shortcut acts behind it. */
export const batchReviewOpen = signal(false);

/** "Select" was tapped: rows that can be sent together show a checkbox (PH-06b). */
export const selectMode = signal(false);

export function setSelectMode(on: boolean): void {
  selectMode.value = on;
  if (!on) batchSelected.value = new Set();
}

/**
 * Whether a card may be sent together with others. A card with attachments, a group text and a Jarvis action each
 * need their own look, so they are never in a batch (audit #4).
 */
export function canBatch(a: Approval): boolean {
  if (mustReviewAlone(a)) return false;
  const kind = a.sends_from?.kind;
  return kind === 'main_line' || kind === 'h2o_line' || kind === 'email';
}

/**
 * A card known to need its own look: attachments, a group text, an email to several people or with a CC, a Jarvis
 * action, or a line the server could not name. A ticked card that turns into one of these leaves the batch.
 */
export function mustReviewAlone(a: Approval): boolean {
  const d = (a.draft_content || {}) as Record<string, unknown>;
  if (Array.isArray(d.attachments) && d.attachments.length > 0) return true;
  if (isGroupText(a)) return true;
  if (a.action_type === 'send_email' && (d.cc || (Array.isArray(d.to_list) && d.to_list.length > 1)
    || (typeof d.to === 'string' && /[,;]/.test(d.to)))) return true;
  const kind = a.sends_from?.kind;
  return kind === 'not_a_message' || kind === 'unknown';
}

/** A text to several people at once (draft is_group or a to_list of two or more). */
export function isGroupText(a: Approval): boolean {
  // An email to several people is an email, never a group text (the approval writer sets is_group on both).
  if (a.action_type === 'send_email') return false;
  const d = (a.draft_content || {}) as Record<string, unknown>;
  if (d.is_group === true) return true;
  if (Array.isArray(d.to_list) && d.to_list.length > 1) return true;
  return a.action_type !== 'send_email' && typeof d.to === 'string' && /[,;]/.test(d.to);
}

/** The people a group text goes to, as written on the card. */
export function groupRecipients(a: Approval): string[] {
  const d = (a.draft_content || {}) as Record<string, unknown>;
  const list: unknown[] = Array.isArray(d.to_list) && d.to_list.length > 0
    ? d.to_list
    : typeof d.to === 'string' ? d.to.split(/[,;]/).map((x) => x.trim()).filter(Boolean) : [];
  return list.map((x) => {
    if (typeof x === 'string') return formatRecipient(x);
    if (x && typeof x === 'object') {
      const o = x as Record<string, unknown>;
      const name = typeof o.name === 'string' ? o.name.trim() : '';
      const num = typeof o.phone === 'string' ? o.phone : typeof o.to === 'string' ? o.to : '';
      return name || formatRecipient(num);
    }
    return '';
  }).filter(Boolean);
}

export const pendingCount = computed(() => {
  if (approvalView.value === 'pending') {
    // The list holds at most one page (200); the server total counts the rest (PH-06a, F10).
    return Math.max(approvals.value.length, _pendingCountCache.value);
  }
  return _pendingCountCache.value;
});

const _pendingCountCache = signal(0);

/**
 * Approvals lifted out of the visible queue by an optimistic approve whose decision has
 * not been confirmed yet (held for undo, or in flight). They are still `pending` on the
 * server, so every path that reads the server's truth has to subtract them: the queue
 * list, both pending counts, the draft-edit reaper, and the WS handlers.
 */
const inFlightSends = new Set<string>();

/**
 * Sent and confirmed, but a response that was already in flight when the decision landed
 * can still list the row as pending. Filtered out until a fresh response stops mentioning
 * them, so a decided card cannot flicker back into the queue. Unlike `inFlightSends` these
 * do NOT adjust the pending count: by then the server's own total is correct.
 */
const settledSends = new Set<string>();

/**
 * Refused as out of date: the need behind the card moved, so no tap can approve it. The
 * server still lists the row as pending until its sweep retires it, and putting it back
 * would only invite a tap that fails the same way. Hidden, and left out of the pending
 * count, until the server stops listing it.
 */
const staleSends = new Set<string>();

/**
 * A settled or stale send stops being filtered as soon as the server stops listing it.
 * Only ever called with the server's own pending list: pruning against a list this store
 * already filtered would forget every hidden id on the spot.
 */
function forgetUnlisted(serverPending: Approval[]): void {
  for (const hidden of [settledSends, staleSends]) {
    for (const id of hidden) {
      if (!serverPending.some((a) => String(a.id) === id)) hidden.delete(id);
    }
  }
}

function withoutInFlight(list: Approval[]): Approval[] {
  if (inFlightSends.size === 0 && settledSends.size === 0 && staleSends.size === 0) return list;
  return list.filter((a) => {
    const key = String(a.id);
    return !inFlightSends.has(key) && !settledSends.has(key) && !staleSends.has(key);
  });
}

/** Both counters always move together; a clamp keeps a double-decrement from going negative. */
function setPendingCount(total: number): void {
  const next = Math.max(0, total);
  _pendingCountCache.value = next;
  tabBarPendingCount.value = next;
}

function bumpPendingCount(delta: number): void {
  _pendingCountCache.value = Math.max(0, _pendingCountCache.value + delta);
  tabBarPendingCount.value = Math.max(0, tabBarPendingCount.value + delta);
}

/** Map a DB approval to a UI category tier */
export function mapToUiCategory(a: Approval): 'urgent' | 'nurture' | 'email' {
  if (['email_reply', 'active_client', 'transaction'].includes(a.category)) {
    return 'urgent';
  }
  if (a.action_type === 'send_email') {
    return 'email';
  }
  return 'nurture';
}

/** Check if an approval is Email type */
function isEmailType(a: Approval): boolean {
  const at = (a.action_type || '').toLowerCase();
  return at.includes('email') || at === 'send_email';
}

/**
 * Check if an approval is an SMS/text message type.
 *
 * Positive test only — the old version had a `|| !at.includes('email')`
 * catch-all that bucketed EVERY non-email action_type (schedule_showing,
 * cancel_showing, future non-message actions) under SMS, so the SMS tab/count
 * showed non-SMS actions in an SMS mental model (pwa-console-v2-9). Now a
 * non-message action is neither SMS nor email and falls into the `other`
 * bucket instead of inflating the SMS count.
 */
function isSmsType(a: Approval): boolean {
  const at = (a.action_type || '').toLowerCase();
  return at.includes('sms') || at.includes('text') || at === 'send_sms';
}

/** A card about a live client, a deal or an email reply: the queue's "Needs you now" section. */
export function isNeedsYouNow(a: Approval): boolean {
  return mapToUiCategory(a) === 'urgent';
}

/**
 * The order the queue shows: "Needs you now", then "Can wait", server order within each (PH-06a F1, PH-06b).
 * Texts and emails are no longer separate sections; each row says its channel.
 */
function inScreenOrder(list: Approval[]): Approval[] {
  if (approvalView.value !== 'pending') return list;
  return list
    .map((a, i) => ({ a, i, t: isNeedsYouNow(a) ? 0 : 1 }))
    .sort((x, y) => x.t - y.t || x.i - y.i)
    .map((x) => x.a);
}

/** The cards in view, in the order on screen; next card, j/k and the list all read this. */
export const filteredApprovals = computed(() => inScreenOrder(filteredByCategory(approvals.value)));

function filteredByCategory(list: Approval[]): Approval[] {
  const cat = activeCategory.value;
  if (cat === 'all') return list;
  if (cat === 'sms') return list.filter((a) => isSmsType(a));
  if (cat === 'email') return list.filter((a) => isEmailType(a));
  if (cat === 'other') return list.filter((a) => !isSmsType(a) && !isEmailType(a));
  if (cat === 'urgent') {
    return list.filter((a) =>
      ['email_reply', 'active_client', 'transaction'].includes(a.category),
    );
  }
  // nurture = everything that's not urgent and not email
  return list.filter(
    (a) =>
      !['email_reply', 'active_client', 'transaction'].includes(a.category) &&
      !isEmailType(a),
  );
}

export const categoryCounts = computed(() => {
  const counts: Record<string, number> = { all: 0, urgent: 0, sms: 0, email: 0, nurture: 0, other: 0 };
  for (const a of approvals.value) {
    counts.all++;
    const tier = mapToUiCategory(a);
    if (tier === 'urgent') counts.urgent++;
    // Positive bucketing: email -> email, sms/text -> sms, everything else
    // (schedule_showing, etc.) -> other. Non-message types no longer inflate
    // the SMS count (pwa-console-v2-9).
    if (isEmailType(a)) {
      counts.email++;
    } else if (isSmsType(a)) {
      counts.sms++;
    } else {
      counts.other++;
    }
    if (tier === 'nurture') counts.nurture++;
  }
  return counts;
});

/** The current approval object (for detail view) */
export const currentApproval = computed(() => {
  const id = currentApprovalId.value;
  if (!id) return null;
  return approvals.value.find((a) => String(a.id) === String(id)) ?? null;
});

// ---- Actions ----

let approvalsLoadInFlight: Promise<void> | null = null;
export function loadApprovals(): Promise<void> {
  if (approvalsLoadInFlight) return approvalsLoadInFlight;
  const view = approvalView.value;
  const before = new Map(approvals.value.map(approval => [String(approval.id), approval]));
  approvalsLoadInFlight = (async () => {
  loading.value = true;
  error.value = null;
  try {
    // A refresh must never resurrect a card Joseph already sent. An optimistically
    // approved approval is still `pending` on the server until its held send commits,
    // so the server list has to be filtered through the in-flight set on the way in.
    if (view === 'pending') {
      const data = await api.getApprovals('pending', { limit: 200 });
      if (approvalView.value !== view) return;
      forgetUnlisted(data.approvals);
      const fresh = withoutInFlight(data.approvals);
      const current = new Map(approvals.value.map(approval => [String(approval.id), approval]));
      const merged = fresh.filter(approval => !before.has(String(approval.id)) || current.has(String(approval.id)))
        .map(approval => {
          const id = String(approval.id);
          const local = current.get(id);
          return local && local !== before.get(id) ? local : approval;
        });
      const ids = new Set(merged.map(approval => String(approval.id)));
      approvals.value = withoutInFlight([...approvals.value.filter(approval =>
        !before.has(String(approval.id)) && !ids.has(String(approval.id))), ...merged]);
      // The server's total, less the listed cards hidden here (sent, waiting to send, changed), plus cards shown
      // here that the server's page did not list yet.
      const listed = new Set(data.approvals.map((approval) => String(approval.id)));
      const hidden = data.approvals.length - fresh.length;
      const localOnly = approvals.value.filter((approval) => !listed.has(String(approval.id))).length;
      setPendingCount(data.total - hidden + localOnly);
      void settleSendFailures(data.approvals);
    } else {
      const [data, pendingData] = await Promise.all([
        api.getApprovals('recent'),
        api.getApprovals('pending', { limit: 200 }),
      ]);
      if (approvalView.value !== view) return;
      approvals.value = data.approvals;
      forgetUnlisted(pendingData.approvals);
      setPendingCount(pendingData.total - inFlightSends.size - staleSends.size);
    }
    // Drop edits for approvals that are no longer in the queue (decided elsewhere).
    // An optimistically-sent approval is NOT "decided elsewhere": its edit is still
    // riding out on a held send, and clearing it here would send the ORIGINAL body.
    for (const id of Object.keys(draftEdits.value)) {
      if (inFlightSends.has(id)) continue;
      if (!approvals.value.some((a) => String(a.id) === id)) clearDraftEdit(id);
    }

    // Prune stale batch selections
    if (batchSelected.value.size > 0) {
      const validIds = new Set(approvals.value.map((a) => a.id));
      const next = new Set<string>();
      for (const id of batchSelected.value) {
        if (validIds.has(id)) next.add(id);
      }
      if (next.size !== batchSelected.value.size) {
        batchSelected.value = next;
      }
    }
  } catch (e) {
    error.value = e instanceof Error ? e.message : 'Failed to load approvals';
  } finally {
    loading.value = false;
    approvalsLoadInFlight = null;
  }
  })();
  return approvalsLoadInFlight;
}

export function setApprovalView(view: 'pending' | 'messages'): void {
  if (approvalView.value === view) return;
  approvalView.value = view;
  setSelectMode(false);
  activeCategory.value = 'all';
  currentApprovalId.value = null;
  // Messages tab loads its own data via messages-store
  if (view !== 'messages') {
    loadApprovals();
  }
}

export function setActiveCategory(cat: string): void {
  activeCategory.value = cat;
  batchSelected.value = new Set();
}

/** Take a row out of the visible queue and the pending count, if it is there. */
function dropFromQueue(id: string): void {
  if (!approvals.value.some((a) => String(a.id) === id)) return;
  approvals.value = approvals.value.filter((a) => String(a.id) !== id);
  bumpPendingCount(-1);
  if (batchSelected.value.has(id)) {
    const next = new Set(batchSelected.value);
    next.delete(id);
    batchSelected.value = next;
  }
}

/**
 * Per-card read counter: only the newest re-read (or a changed refusal's current fields,
 * which bump it too) may write the card, so an earlier read that answers late cannot put
 * older wording back on screen (B1c).
 */
const _readSeq = new Map<string, number>();
function nextReadSeq(id: string): number {
  const seq = (_readSeq.get(String(id)) ?? 0) + 1;
  _readSeq.set(String(id), seq);
  return seq;
}

/** Re-read one approval after another Jarvis lane rewrites it outside this browser. */
export async function refreshApproval(id: string): Promise<void> {
  const seq = nextReadSeq(id);
  try {
    const fresh = (await api.getApproval(id)) as unknown as Approval;
    if (_readSeq.get(String(id)) !== seq) return;
    // Decided or withdrawn somewhere else since the queue was read. A card that can no
    // longer be approved must leave the pending queue, never wait there for a tap that
    // can only fail.
    if (approvalView.value === 'pending' && String(fresh.status) !== 'pending') {
      dropFromQueue(String(id));
      settleNoticeForDecidedRow(String(id), String(fresh.status));
      return;
    }
    approvals.value = approvals.value.map((approval) =>
      String(approval.id) === String(id) ? fresh : approval,
    );
  } catch (e) {
    // Keep the last usable queue copy. The normal queue refresh remains available.
    console.error('[approvals] Failed to refresh selected approval:', e);
  }
}

export function setCurrentApproval(id: string | null): void {
  openedApproval(id); // PH-03e: opened from Today's Needs you, or not
  currentApprovalId.value = id;
  if (id) void refreshApproval(id);
}

/** Open a card with its decline sheet showing (a row swiped left). Nothing is declined until he chooses. */
export function openToDecline(id: string): void {
  declineRequestedId.value = id;
  setCurrentApproval(id);
}

// ---- Draft edits ----
//
// An edit used to live in ApprovalDetail's useState and was only ever sent if that exact
// component instance dispatched the approve. Switching tabs, reloading, swiping the row,
// or batch-approving discarded it and shipped the ORIGINAL body. Edits now live here
// (module scope survives view remounts) and are pushed to the server, which is the only
// copy the send path reads.

export type DraftSaveState = 'idle' | 'saving' | 'saved' | 'error';

/** Edited bodies keyed by approval id, whether or not the server has them yet. */
export const draftEdits = signal<Record<string, string>>({});

/** Save status per approval id. Global status bled across approvals and misreported. */
export const draftSaveStates = signal<Record<string, DraftSaveState>>({});

/**
 * A voice edit per card: listening or rewriting. Kept here, not in the mic button, so a rewrite still running keeps
 * Approve waiting when the button is hidden (decline sheet) and comes back (PH-06a F4, checker-1 yellow).
 */
export type VoiceEditState = 'idle' | 'recording' | 'processing';
export const voiceEditStates = signal<Record<string, VoiceEditState>>({});

export function voiceEditStateFor(id: string): VoiceEditState {
  return voiceEditStates.value[String(id)] ?? 'idle';
}

export function setVoiceEditState(id: string, state: VoiceEditState): void {
  const key = String(id);
  const next = { ...voiceEditStates.value };
  if (state === 'idle') delete next[key];
  else next[key] = state;
  voiceEditStates.value = next;
}

export function draftSaveStateFor(id: string): DraftSaveState {
  return draftSaveStates.value[String(id)] ?? 'idle';
}

function setSaveState(id: string, state: DraftSaveState): void {
  draftSaveStates.value = { ...draftSaveStates.value, [id]: state };
}

// Each save re-renders the approval's Telegram card, so idle-save slowly. Correctness
// does not depend on this timer: flushDraftEdit() always runs before an approve.
const DRAFT_SAVE_DEBOUNCE_MS = 1500;
const _unsaved = new Set<string>();
const _saveTimers = new Map<string, ReturnType<typeof setTimeout>>();
const _saveChains = new Map<string, Promise<void>>();
/**
 * Bumped whenever an edit is dropped (clearDraftEdit). A save that answers after that must
 * not write its text back onto the card: a changed refusal may have put the current
 * wording there in the meantime (B1c).
 */
const _editGeneration = new Map<string, number>();
const editGeneration = (id: string): number => _editGeneration.get(id) ?? 0;

/** Body currently displayed for an approval: the edit if any, else the stored draft. */
export function draftBodyFor(a: Approval): string {
  const edit = draftEdits.value[String(a.id)];
  if (edit !== undefined) return edit;
  const stored = (a.draft_content as { body?: string } | undefined)?.body;
  return stored ?? '';
}

function storedBodyFor(id: string): string {
  const a = approvals.value.find((x) => String(x.id) === String(id));
  return (a?.draft_content as { body?: string } | undefined)?.body ?? '';
}

/** Rewrite the local approval so a later reload/remount shows the edit, not the original. */
function applySavedBody(id: string, body: string): void {
  approvals.value = approvals.value.map((a) =>
    String(a.id) === String(id)
      ? ({ ...a, draft_content: { ...(a.draft_content as object), body } } as Approval)
      : a,
  );
}

function clearDraftEdit(id: string): void {
  _editGeneration.set(id, editGeneration(id) + 1);
  const timer = _saveTimers.get(id);
  if (timer) clearTimeout(timer);
  _saveTimers.delete(id);
  _unsaved.delete(id);
  _saveChains.delete(id);
  if (draftEdits.value[id] !== undefined) {
    const next = { ...draftEdits.value };
    delete next[id];
    draftEdits.value = next;
  }
  if (draftSaveStates.value[id] !== undefined) {
    const next = { ...draftSaveStates.value };
    delete next[id];
    draftSaveStates.value = next;
  }
}

/** Serialize saves per approval so a slow request cannot overwrite a newer one. */
function queueSave(id: string, opts?: { keepalive?: boolean }): Promise<void> {
  const prev = _saveChains.get(id) ?? Promise.resolve();
  const next = prev.catch(() => {}).then(async () => {
    if (!_unsaved.has(id)) return;
    const body = draftEdits.value[id];
    if (body === undefined) return;
    // An empty body is never savable or sendable (the server rejects it). Never POST it;
    // leave the edit marked unsaved so an approve stays blocked and flushDraftEdit reports
    // the empty case with a clear message.
    if (body.trim() === '') {
      _unsaved.add(id);
      return;
    }
    setSaveState(id, 'saving');
    const generation = editGeneration(id);
    try {
      // Only pass the options object when there is one, so the ordinary save keeps the
      // exact call shape the rest of the app (and its tests) already rely on.
      const res = opts
        ? await api.saveApprovalDraft(id, body, opts)
        : await api.saveApprovalDraft(id, body);
      // The edit was dropped while this save was out (decided, or the card changed).
      if (editGeneration(id) !== generation) return;
      const stored = res.body ?? body;
      applySavedBody(id, stored);
      // Only settle if nothing newer was typed while the request was in flight. Adopt the
      // server's copy so the text on screen is exactly the text that will be sent.
      if (draftEdits.value[id] === body) {
        if (stored !== body) draftEdits.value = { ...draftEdits.value, [id]: stored };
        _unsaved.delete(id);
        setSaveState(id, 'saved');
      }
    } catch (e) {
      setSaveState(id, 'error');
      throw e;
    }
  });
  _saveChains.set(id, next);
  return next;
}

/** Record a keystroke-level edit and schedule a debounced save. */
export function setDraftEdit(id: string, body: string): void {
  // The card is already dismissed and its save is in flight. A late edit (a voice-edit
  // callback resolving after the tap) must not schedule a save that races it.
  if (inFlightSends.has(String(id))) return;

  const timer = _saveTimers.get(id);
  if (timer) clearTimeout(timer);

  draftEdits.value = { ...draftEdits.value, [id]: body };

  if (body === storedBodyFor(id)) {
    // Typed back to the stored text: nothing to persist.
    _saveTimers.delete(id);
    _unsaved.delete(id);
    setSaveState(id, 'idle');
    return;
  }

  if (body.trim() === '') {
    // Clearing all the text is a transient editing state (about to retype), never a body to
    // save or send. Do NOT schedule a save the server would reject: that would flash a red
    // error banner mid-edit. Keep it marked unsaved so an approve stays blocked, and hold the
    // save state at idle so the UI stays quiet until real text returns.
    _saveTimers.delete(id);
    _unsaved.add(id);
    setSaveState(id, 'idle');
    return;
  }

  _unsaved.add(id);
  setSaveState(id, 'saving');
  _saveTimers.set(
    id,
    setTimeout(() => {
      _saveTimers.delete(id);
      void queueSave(id).catch(() => {});
    }, DRAFT_SAVE_DEBOUNCE_MS),
  );
}

/**
 * Approve a card whose edit is not yet persisted, in ONE request.
 *
 * The two-request shape (save, await it, then decide) is how an approve gets lost. If the
 * PWA is backgrounded or the network flaps while the /draft POST is in flight, the browser
 * aborts that request; the save chain rejects, performDecide throws before it ever reaches
 * api.decideApproval, and NO decide is issued at all. The row stays `pending`, nothing
 * sends, and the failure banner renders into a page Joseph is no longer looking at. Four
 * of those on 2026-09-04 alone (caddy logged the aborted saves as `status: 0`).
 *
 * The server applies the same gated body write first and only then commits the decision,
 * so send-time still re-reads the edited body from the row. One request cannot be
 * half-issued, which is the whole point.
 *
 * Returns false when there was nothing unsaved, so the caller takes the ordinary path.
 */
async function saveAndApprove(
  id: string,
  opts?: { keepalive?: boolean },
  shown?: ApprovalShownFields,
): Promise<boolean> {
  const timer = _saveTimers.get(id);
  if (timer) {
    clearTimeout(timer);
    _saveTimers.delete(id);
  }
  if (!_unsaved.has(id)) return false;
  const body = draftEdits.value[id];
  if (body === undefined || body.trim() === '') return false;

  // Deliberately NOT chained behind the in-flight save for this row, which is what the
  // ordinary saves do: waiting on a request that may never answer -- the page going away
  // is the whole reason we are on this path -- is precisely the chain that stranded the
  // approve. Go straight out.
  //
  // Un-chaining does mean an OLDER save can still be in flight carrying the text Joseph
  // typed before his last keystroke, and it could land after this one. What makes that
  // safe is server-side, not this ordering: POST /draft passes the body it read back as a
  // compare-and-set on the decide, so the decision can only commit onto the exact text it
  // verified. A late older write turns the approve into a 409, never a wrong send.
  //
  // Always keepalive: this request has to land even if the document is torn down the
  // instant after it is issued. Draft bodies are capped at 10k chars, far inside the
  // keepalive budget.
  setSaveState(id, 'saving');
  const generation = editGeneration(id);
  const next = (async () => {
    try {
      const res = await api.saveApprovalDraft(id, body, {
        ...opts,
        keepalive: true,
        decide: 'approve',
        shown,
      });
      if (editGeneration(id) !== generation) return;
      applySavedBody(id, res.body ?? body);
      _unsaved.delete(id);
      setSaveState(id, 'saved');
    } catch (e) {
      setSaveState(id, 'error');
      throw e;
    }
  })();
  _saveChains.set(id, next);
  await next;
  return true;
}

/**
 * Push any outstanding edit for `id` to the server and wait for it. Throws if the edit
 * cannot be saved, so an approve aborts rather than sending a body Joseph never wrote.
 */
export async function flushDraftEdit(
  id: string,
  opts?: { keepalive?: boolean },
): Promise<void> {
  const timer = _saveTimers.get(id);
  if (timer) {
    clearTimeout(timer);
    _saveTimers.delete(id);
  }
  // An empty edit can never be sent. Block the approve with a clear reason instead of
  // posting a body the server will reject.
  const pending = draftEdits.value[id];
  if (pending !== undefined && pending.trim() === '') {
    setSaveState(id, 'error');
    throw new Error('Draft cannot be empty');
  }
  if (_unsaved.has(id)) await queueSave(id, opts);
  else await (_saveChains.get(id) ?? Promise.resolve());
  if (_unsaved.has(id)) throw new Error('Draft edit could not be saved');
}

/**
 * What the card shows right now (B1c): its title, the body in the editor (the saved text once
 * a save has been adopted), its subject and its attachments exactly as the server sent them.
 * An approve carries these and the server commits only onto exactly the same fields, so a
 * card rewritten after Joseph read it is refused instead of sent.
 */
export function shownFieldsOf(row: Approval): ApprovalShownFields {
  const draft = (row.draft_content ?? {}) as Record<string, unknown>;
  const edit = draftEdits.value[String(row.id)];
  return {
    title: row.title,
    body: edit !== undefined ? edit : draft.body,
    subject: draft.subject,
    attachments: draft.attachments,
  };
}

/** The card with the current fields a 409 changed refusal carried, so no further read is needed. */
function withCurrentFields(row: Approval, current: unknown): Approval {
  if (!current || typeof current !== 'object') return row;
  const fields = current as Record<string, unknown>;
  const draft = { ...((row.draft_content ?? {}) as Record<string, unknown>) };
  for (const key of ['body', 'subject', 'attachments']) {
    if (fields[key] === undefined || fields[key] === null) delete draft[key];
    else draft[key] = fields[key];
  }
  const title = typeof fields.title === 'string' ? fields.title : row.title;
  return { ...row, title, draft_content: draft } as Approval;
}

function changedCurrentOf(e: unknown): unknown {
  return (e as { data?: { current?: unknown } } | null)?.data?.current;
}

/**
 * The network half of a decision: persist any edit, then decide. Mutates no list, so the
 * optimistic approve path -- which removed the row seconds earlier -- can share it.
 *
 * The edit is persisted BEFORE the decision because execute_approved.py re-reads
 * draft_content from the DB at send time (execute_approved.py:393). Save and decide can
 * never be parallelised: a decide that overtakes its save sends the ORIGINAL body.
 *
 * Deliberately no edited_body on the decide call. The server's edited_body branch writes
 * the body inside the decide transaction and skips every content gate the /draft path
 * applies (sanitize, currency integrity, branded-link, comps, operator immutability).
 * The row written by /draft is the only gated copy.
 */
async function performDecide(
  id: string,
  action: string,
  decline?: { declineReason?: string; declineAudioPath?: string },
  opts?: { keepalive?: boolean },
  shownAtTap?: ApprovalShownFields,
): Promise<void> {
  if (action === 'approve') {
    // An empty edit can never be sent. Keep this check ahead of the combined request so
    // the reason is the same one flushDraftEdit gives.
    const pendingEdit = draftEdits.value[id];
    if (pendingEdit !== undefined && pendingEdit.trim() === '') {
      setSaveState(id, 'error');
      throw new Error('Draft cannot be empty');
    }
    // The card as shown at the tap: captured by the optimistic approve when it dismissed the
    // card, otherwise read here, before any save can adopt other text.
    const row = approvals.value.find((a) => String(a.id) === String(id));
    const shown = shownAtTap ?? (row ? shownFieldsOf(row) : undefined);
    if (await saveAndApprove(id, opts, shown)) {
      clearDraftEdit(id);
      return;
    }
    await flushDraftEdit(id, opts);
    await api.decideApproval(id, action, undefined, { ...opts, shown });
    clearDraftEdit(id);
    return;
  }

  // An approve never carries decline feedback. Options are appended only when set, so the
  // ordinary decide keeps the exact call shape the rest of the app already relies on.
  const declineArg = action === 'approve' ? undefined : decline;
  if (opts) {
    await api.decideApproval(id, action, declineArg, opts);
  } else if (declineArg === undefined) {
    await api.decideApproval(id, action);
  } else {
    await api.decideApproval(id, action, declineArg);
  }
  clearDraftEdit(id);
}

export async function decideApproval(
  id: string,
  action: string,
  decline?: { declineReason?: string; declineAudioPath?: string },
  shownAtTap?: ApprovalShownFields,
): Promise<{ nextId: string | null }> {
  const filtered = filteredApprovals.value;
  const currentIdx = filtered.findIndex((a) => String(a.id) === String(id));
  const nextApproval = filtered[currentIdx + 1] || filtered[currentIdx - 1] || null;

  try {
    await performDecide(id, action, decline, undefined, shownAtTap);
  } catch (e) {
    // The card changed after it was shown (B1c): nothing was decided. Put the current
    // wording on screen and say so; he reads it and approves again.
    if (isChangedRefusal(e)) {
      const row = approvals.value.find((a) => String(a.id) === String(id));
      clearDraftEdit(String(id));
      // The refusal carries the current fields: show them at once, then re-read the card.
      const current = changedCurrentOf(e);
      nextReadSeq(String(id));
      approvals.value = approvals.value.map((a) =>
        String(a.id) === String(id) ? withCurrentFields(a, current) : a,
      );
      reportSendFailure({
        id: String(id),
        contact: row ? extractContactName(row) : '',
        kind: 'changed',
        message: CHANGED_REASON,
      });
      void refreshApproval(String(id));
      throw e;
    }
    // Same rule as the optimistic approve below: a lost answer is not a lost decision.
    const landed = action === 'approve' ? SENT_STATUSES : DECLINED_STATUSES;
    const fate = await fateAfterFailure(String(id), landed, isUnanswered(e));
    if (fate.fate === 'gone') {
      dropFromQueue(String(id));
      clearDraftEdit(String(id));
      void loadApprovals();
    }
    if (fate.fate !== 'landed') throw e;
    clearDraftEdit(String(id));
  }

  // Remove from local list immediately for snappy feel
  dropFromQueue(String(id));
  // An earlier failed attempt on this card no longer describes it.
  dismissSendFailure(String(id));

  const nextId =
    nextApproval && approvals.value.some((a) => String(a.id) === String(nextApproval.id))
      ? String(nextApproval.id)
      : null;

  return { nextId };
}

// ---- What really happened to a failed decision ----
//
// A failed request is not a failed send. The server commits a decision whether or not the
// phone is still listening. On 2026-10-06 and 2026-10-07 three approves lost their
// connection while a slow save was in flight; the app said "Not sent ... It is back in the
// queue", the server sent all three, and the re-tap answered 409 and painted a second
// failure for a message already delivered. Only the queue row knows the outcome, so every
// failure path reads it before it says anything.

/** The approve landed: the message is going out or already has. */
const SENT_STATUSES: ReadonlySet<string> = new Set(['approved', 'executed', 'delivering', 'sent']);

/** The decline landed (a skip is stored as rejected too). */
const DECLINED_STATUSES: ReadonlySet<string> = new Set(['rejected']);

/**
 * Waits between reads of the row while a decision's fate is unknown. A request that died
 * without an answer may still be running on the server (a save is about a second, its
 * worst case far longer), so the row is read again before the card is put back.
 */
export const SEND_VERDICT_DELAYS_MS: readonly number[] = [1500, 3000, 5000, 8000];

interface RowFate {
  /** landed: the decision is on the row. gone: the row left the queue some other way. */
  fate: 'landed' | 'pending' | 'gone' | 'unknown';
  status?: string;
}

function httpStatusOf(e: unknown): number | null {
  const status = (e as { status?: unknown } | null)?.status;
  return typeof status === 'number' ? status : null;
}

/** fetch rejects with a TypeError (or an abort) when the connection dies before any answer. */
function isDroppedConnection(e: unknown): boolean {
  return e instanceof TypeError || (e as { name?: unknown } | null)?.name === 'AbortError';
}

/** The proxy's answer when the app server behind it is down, restarting or too slow. */
const GATEWAY_STATUSES: ReadonlySet<number> = new Set([502, 503, 504]);

/**
 * The app server itself never answered: the connection died, the wait ran out, or the
 * proxy answered for it. The request may still be running there, or may land later, so
 * none of these says the decision failed.
 */
function isUnanswered(e: unknown): boolean {
  if (isDroppedConnection(e) || e instanceof SendTimeoutError) return true;
  const status = httpStatusOf(e);
  return status !== null && GATEWAY_STATUSES.has(status);
}

/** The server's refusal of an approve whose card changed after it was shown (B1c). */
function isChangedRefusal(e: unknown): boolean {
  const data = (e as { data?: { status?: unknown } } | null)?.data;
  return httpStatusOf(e) === 409 && data?.status === 'changed';
}

/** The server's own refusal of a card whose need moved after the draft was written. */
function isStaleRefusal(e: unknown): boolean {
  const data = (e as { data?: { status?: unknown } } | null)?.data;
  return httpStatusOf(e) === 409 && data?.status === 'stale';
}

async function readFate(id: string, landed: ReadonlySet<string>): Promise<RowFate> {
  try {
    const fresh = (await api.getApproval(id)) as unknown as { status?: unknown };
    const status = String(fresh?.status ?? '');
    if (landed.has(status)) return { fate: 'landed', status };
    if (status === 'pending') return { fate: 'pending', status };
    return { fate: 'gone', status };
  } catch (e) {
    return httpStatusOf(e) === 404 ? { fate: 'gone', status: 'missing' } : { fate: 'unknown' };
  }
}

/**
 * Read the row after a failed decision. `patient` is for a request that got no answer at
 * all: the server may still be working on it, so a pending or unreadable row is read again
 * over SEND_VERDICT_DELAYS_MS. A request the server answered has nothing left in flight,
 * so one read is the verdict.
 */
async function fateAfterFailure(
  id: string,
  landed: ReadonlySet<string>,
  patient: boolean,
): Promise<RowFate> {
  let fate = await readFate(id, landed);
  if (!patient) return fate;
  for (const delay of SEND_VERDICT_DELAYS_MS) {
    if (fate.fate === 'landed' || fate.fate === 'gone') break;
    await new Promise((resolve) => setTimeout(resolve, delay));
    fate = await readFate(id, landed);
  }
  return fate;
}

/** Why a card that left the queue did not send, in words Joseph can act on. */
function withdrawnReason(status: string | undefined): string {
  switch (status) {
    case 'superseded':
      return 'This draft was replaced before you approved it, so nothing went out';
    case 'expired':
      return 'This draft expired before you approved it, so nothing went out';
    case 'rejected':
      return 'This draft was already declined, so nothing went out';
    case 'send_failed':
    case 'blocked':
      return 'It was approved, but the send did not go through';
    default:
      return 'This draft is no longer in the queue, so nothing went out';
  }
}

const STALE_REASON =
  'This draft was out of date, so nothing went out. If a reply is still needed, a new draft will appear';

const DROPPED_REASON = 'The connection dropped before it reached the server';

const CHANGED_REASON =
  'This card changed after you opened it. The current wording is now on screen. Read it and approve again.';

/** The reason shown when the card goes back into the queue. */
function requeuedReason(e: unknown): string {
  if (isDroppedConnection(e)) return DROPPED_REASON;
  if (GATEWAY_STATUSES.has(httpStatusOf(e) ?? 0)) return 'The server did not answer';
  return e instanceof Error && e.message ? e.message : 'Send failed';
}

// ---- Optimistic approve with an undo window ----
//
// Joseph works this queue on a phone and wants to fire through it. The old detail flow
// made him wait out a window.confirm, the /draft round trip (a python exec whose Telegram
// card re-render alone measures ~0.5s), the /decide round trip, and a hardcoded 400ms
// animation before the next card appeared: two to three seconds and an extra tap per
// message, strictly one message at a time.
//
// The card now clears INSTANTLY and the decision commits at the END of a 5s undo window.
// This mirrors the proven Today-tab model in modules/today/today-actions.ts: because the
// backend effect fires at the end of the window, Undo simply never fires it and no
// un-send endpoint is needed.
//
// On this path it is strictly SAFER than the modal it replaces. window.confirm bought one
// tap of hesitation and then an irreversible send with zero recall; there are now five
// seconds of real recall. Autonomy is unchanged: every send is still Joseph's own tap.

/** Recall window for an optimistic approve. Exported so the pill can drain in step with it. */
export const SEND_UNDO_WINDOW_MS = 5000;

/**
 * How long a dispatched approve may go unanswered before it is treated as failed.
 *
 * `api.request` uses a bare fetch with no timeout, and a dead-but-not-closed mobile
 * connection never settles it. That is worse than a plain failure: the `.catch` never runs
 * so no banner appears, the `.finally` never runs so `inFlightSends` keeps the id forever,
 * and `withoutInFlight` then hides the row from the queue AND subtracts it from the badge.
 * The card silently ceases to exist until a full reload. Generous on purpose: a real
 * save-and-approve round trip is one to two seconds, so this only fires on a hung socket.
 *
 * The request is keepalive, so giving up waiting does not cancel it. If it lands anyway the
 * restored row refreshes to its canonical `approved` state, which is the honest outcome.
 */
export const SEND_TIMEOUT_MS = 30_000;

/** The wait for an answer ran out. The request is keepalive and may still land. */
class SendTimeoutError extends Error {}

function withSendTimeout<T>(work: Promise<T>): Promise<T> {
  let timer: ReturnType<typeof setTimeout>;
  return Promise.race([
    work,
    new Promise<never>((_, reject) => {
      timer = setTimeout(
        () => reject(new SendTimeoutError('Send timed out, check this one')),
        SEND_TIMEOUT_MS,
      );
    }),
  ]).finally(() => clearTimeout(timer)) as Promise<T>;
}

export interface SendFailure {
  id: string;
  contact: string;
  message: string;
  /**
   * requeued (the default): not sent, and the card is back in the queue.
   * withdrawn: not sent, and the card is gone (replaced, expired, declined, out of date).
   * unconfirmed: the server could not be reached, so whether it sent is not known yet.
   * changed: not sent, because the card changed after it was shown; it is back with the
   * current wording (B1c).
   */
  kind?: 'requeued' | 'withdrawn' | 'unconfirmed' | 'changed';
}

/**
 * Sends that failed after their card was already dismissed. Joseph has moved on by then,
 * so the failure has to persist on screen until he acts on it. Optimism without a loud
 * failure surface is exactly how a message silently never goes out.
 */
export const sendFailures = signal<SendFailure[]>([]);

export function dismissSendFailure(id: string): void {
  sendFailures.value = sendFailures.value.filter((f) => f.id !== String(id));
}

function reportSendFailure(failure: SendFailure): void {
  sendFailures.value = [...sendFailures.value.filter((f) => f.id !== failure.id), failure];
}

/**
 * Keep each notice true to the row. Runs after a queue load succeeds, which also proves
 * the server is reachable again. An unconfirmed send is read and settled: gone if it sent
 * after all, withdrawn if the card left the queue, requeued if it is still waiting. A
 * requeued card the server no longer lists (a late request landed, or the card was
 * retired) is settled the same way instead of promising it "is back in the queue".
 */
async function settleSendFailures(serverPending: Approval[]): Promise<void> {
  const listed = new Set(serverPending.map((a) => String(a.id)));
  for (const failure of sendFailures.value) {
    const kind = failure.kind ?? 'requeued';
    if (kind === 'withdrawn' || (kind === 'requeued' && listed.has(failure.id))) continue;
    const fate = await readFate(failure.id, SENT_STATUSES);
    // Dismissed, or replaced by a newer notice, while the row was being read.
    if (!sendFailures.value.includes(failure)) continue;
    if (fate.fate === 'landed') {
      dropFromQueue(failure.id);
      clearDraftEdit(failure.id);
      dismissSendFailure(failure.id);
    } else if (fate.fate === 'gone') {
      dropFromQueue(failure.id);
      clearDraftEdit(failure.id);
      reportSendFailure({ ...failure, kind: 'withdrawn', message: withdrawnReason(fate.status) });
    } else if (fate.fate === 'pending' && kind === 'unconfirmed') {
      reportSendFailure({ ...failure, kind: 'requeued', message: DROPPED_REASON });
    }
  }
}

/**
 * A returned card was read again and its row is no longer pending. Its notice can no
 * longer promise "back in the queue": a late request landed (no notice at all), or the
 * card left the queue without sending.
 */
function settleNoticeForDecidedRow(id: string, status: string): void {
  const failure = sendFailures.value.find((f) => f.id === id);
  if (!failure || failure.kind === 'withdrawn') return;
  clearDraftEdit(id);
  if (SENT_STATUSES.has(status)) {
    dismissSendFailure(id);
  } else {
    reportSendFailure({ ...failure, kind: 'withdrawn', message: withdrawnReason(status) });
  }
}

export interface SendUndoState {
  message: string;
  undo: () => void;
}

/** The visible undo pill, or null. ApprovalsView renders it. */
export const sendUndoBar = signal<SendUndoState | null>(null);

let heldSend: { id: string; commit: () => void } | null = null;
let holdTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * Dispatch a held send NOW instead of waiting out its window. Called when a second
 * approve supersedes the bar, when the approvals view unmounts, and when the page is
 * hidden or unloaded. Without those hooks a backgrounded PWA would drop the send on the
 * floor having already told Joseph it was sent.
 *
 * `unloading` marks the request keepalive so it still reaches the server after the page
 * goes away. This matters more than it looks: a send that never lands leaves the row
 * `pending`, and brain.py's stale-approval sweep (every :07/:37) silently expires a
 * pending send_text once any outgoing message reaches that contact. A dropped request is
 * therefore not merely "try again later", it can be a permanently lost message.
 */
export function flushHeldSend(): void {
  if (holdTimer) {
    clearTimeout(holdTimer);
    holdTimer = null;
  }
  const held = heldSend;
  heldSend = null;
  sendUndoBar.value = null;
  held?.commit();
}

// The PWA is backgrounded far more often than it is closed, and on mobile a backgrounded
// tab can be frozen or discarded without ever firing `unload`. `pagehide` plus a hidden
// `visibilitychange` are the two events that actually fire on iOS and Android.
if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', () => flushHeldSend());
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushHeldSend();
  });
}

/**
 * Put a row back where it was after a failed or undone send.
 *
 * The re-inserted `row` is the snapshot taken when the card was dismissed, and it can be
 * STALE in the one way that matters: `applySavedBody` maps over `approvals.value`, so a
 * draft save that completed while the row was out of the list never reached it. Restoring
 * it verbatim would put the PRE-EDIT body back on screen, `storedBodyFor` would then
 * report that stale text as stored, and setDraftEdit's "typed back to the stored text"
 * branch could drop a real edit. Re-reading the canonical row closes that.
 */
function restoreRow(row: Approval, index: number): void {
  if (approvals.value.some((a) => String(a.id) === String(row.id))) return;
  const next = approvals.value.slice();
  next.splice(Math.min(index, next.length), 0, row);
  approvals.value = next;
  bumpPendingCount(1);
  void refreshApproval(String(row.id));
}

/**
 * Approve optimistically: the row leaves the queue at once and the decision commits when
 * the undo window closes. Returns the id of the card to show next, chosen before the row
 * leaves the list. Never rejects; a failure lands in `sendFailures` and restores the row.
 */
/** What the undo toast says: who, and from which line (PH-06b, audit #7). */
function undoMessage(row: Approval, contact: string): string {
  const who = contact || extractContactName(row);
  const from = row.sends_from;
  // A card that is not a message, or a group text that cannot send from a card (PH-04b), never reads as sending.
  if (from?.kind === 'not_a_message' || from?.kind === 'group_text') return who ? `Approving for ${who}` : 'Approving';
  const line = from && from.kind !== 'unknown' && from.label ? ` ${from.label.charAt(0).toLowerCase()}${from.label.slice(1)}` : '';
  return who ? `Sending to ${who}${line}` : `Sending${line}`;
}

export function approveWithUndo(id: string, contact: string): string | null {
  const key = String(id);
  if (inFlightSends.has(key)) return null; // a double tap must never send twice

  // An emptied draft can never be sent. Refuse BEFORE the card is dismissed, so the block
  // is immediate and visible, rather than surfacing as a failure banner five seconds after
  // Joseph has moved on. This keeps the pre-existing block-before-removal guarantee.
  const pendingEdit = draftEdits.value[key];
  if (pendingEdit !== undefined && pendingEdit.trim() === '') {
    setSaveState(key, 'error');
    return null;
  }

  const list = approvals.value;
  const index = list.findIndex((a) => String(a.id) === key);
  if (index === -1) return null;
  const row = list[index];
  // What the card shows at this tap; a save during the undo window may adopt other text.
  const shownAtTap = shownFieldsOf(row);

  // Pick the next card while the row is still in the filtered list.
  const filtered = filteredApprovals.value;
  const fIdx = filtered.findIndex((a) => String(a.id) === key);
  const neighbour = filtered[fIdx + 1] || filtered[fIdx - 1] || null;
  const nextId = neighbour ? String(neighbour.id) : null;

  // Rapid fire: this approve supersedes the previous bar, so commit that send rather
  // than losing it.
  flushHeldSend();

  inFlightSends.add(key);
  approvals.value = list.filter((a) => String(a.id) !== key);
  bumpPendingCount(-1);
  if (batchSelected.value.has(key)) {
    const next = new Set(batchSelected.value);
    next.delete(key);
    batchSelected.value = next;
  }

  // Start persisting the edit immediately: the undo window is dead time otherwise, and
  // the save has to land before the decide regardless. A failure here is retried by the
  // flush inside performDecide, which is where it surfaces.
  //
  // keepalive because Joseph routinely locks the phone or switches apps inside the undo
  // window. Without it the browser is free to abort this request the moment the page is
  // hidden, which is exactly the abort that used to strand the approve.
  void flushDraftEdit(key, { keepalive: true }).catch(() => {});

  const commit = (): void => {
    void withSendTimeout(performDecide(key, 'approve', undefined, { keepalive: true }, shownAtTap))
      .then(() => {
        settledSends.add(key);
        dismissSendFailure(key);
      })
      .catch(async (e: unknown) => {
        console.error('[approvals] Send request failed after the card was dismissed:', e);
        const who = contact || extractContactName(row);
        if (isChangedRefusal(e)) {
          // The server answered: nothing was decided, the card changed after he read it. It
          // comes back with the current fields the refusal carried, and his edit is dropped.
          clearDraftEdit(key);
          nextReadSeq(key);
          restoreRow(withCurrentFields(row, changedCurrentOf(e)), index);
          reportSendFailure({ id: key, contact: who, kind: 'changed', message: CHANGED_REASON });
          return;
        }
        // The card stays hidden (still in inFlightSends) while the row is read, so it
        // cannot be tapped a second time before its fate is known.
        const fate = await fateAfterFailure(key, SENT_STATUSES, isUnanswered(e));
        if (fate.fate === 'landed') {
          settledSends.add(key);
          clearDraftEdit(key);
          dismissSendFailure(key);
          return;
        }
        if (fate.fate === 'gone' || (fate.fate === 'pending' && isStaleRefusal(e))) {
          if (fate.fate === 'pending') staleSends.add(key);
          clearDraftEdit(key);
          reportSendFailure({
            id: key,
            contact: who,
            kind: 'withdrawn',
            message: fate.fate === 'pending' ? STALE_REASON : withdrawnReason(fate.status),
          });
          void loadApprovals();
          return;
        }
        restoreRow(row, index);
        // No answer to the approve and none to the read: whether it sent is not known.
        reportSendFailure(
          fate.fate === 'unknown' && isUnanswered(e)
            ? { id: key, contact: who, kind: 'unconfirmed', message: 'The server could not be reached' }
            : { id: key, contact: who, kind: 'requeued', message: requeuedReason(e) },
        );
      })
      .finally(() => {
        inFlightSends.delete(key);
      });
  };

  heldSend = { id: key, commit };
  sendUndoBar.value = {
    message: undoMessage(row, contact),
    undo: () => {
      if (holdTimer) {
        clearTimeout(holdTimer);
        holdTimer = null;
      }
      heldSend = null;
      sendUndoBar.value = null;
      inFlightSends.delete(key);
      restoreRow(row, index);
    },
  };
  holdTimer = setTimeout(() => {
    holdTimer = null;
    const held = heldSend;
    heldSend = null;
    sendUndoBar.value = null;
    held?.commit();
  }, SEND_UNDO_WINDOW_MS);

  return nextId;
}

export async function batchDecide(action: string, only?: Array<Approval['id']>): Promise<number> {
  // Select mode passes the cards it showed on the review sheet (batchRows); nothing else goes.
  const ids = only ? [...only] : [...batchSelected.value];
  if (ids.length === 0) return 0;

  // Each approved card carries what it showed at this tap, read before any save below can
  // adopt other text or a refresh can replace a row; the server leaves a card that changed
  // since pending and names it, and approves the rest (B1c).
  const rows = new Map(approvals.value.map((a) => [String(a.id), a]));
  const shown: Record<string, ApprovalShownFields> = {};
  for (const id of ids) {
    const row = rows.get(String(id));
    if (row) shown[id] = shownFieldsOf(row);
  }

  // Batch approve carries no bodies, so any pending edit has to be on the row first.
  if (action === 'approve') {
    for (const id of ids) await flushDraftEdit(id);
  }

  const result = action === 'approve'
    ? await api.batchDecideApprovals(ids, action, shown)
    : await api.batchDecideApprovals(ids, action);
  for (const id of ids) clearDraftEdit(id);
  const changed = new Map((result.changed ?? []).map((c) => [String(c.id), c.current]));
  if (changed.size > 0) {
    // Show the current fields the reply carried at once; the reload below refines them.
    for (const id of changed.keys()) nextReadSeq(id);
    approvals.value = approvals.value.map((a) =>
      changed.has(String(a.id)) ? withCurrentFields(a, changed.get(String(a.id))) : a,
    );
  }
  for (const id of changed.keys()) {
    const row = rows.get(String(id));
    reportSendFailure({
      id: String(id),
      contact: row ? extractContactName(row) : '',
      kind: 'changed',
      message: CHANGED_REASON,
    });
  }
  batchSelected.value = new Set();
  await loadApprovals();
  return result.processed ?? 0;
}

export function toggleBatchSelect(id: string): void {
  const next = new Set(batchSelected.value);
  if (next.has(id)) {
    next.delete(id);
  } else {
    next.add(id);
  }
  batchSelected.value = next;
}

export function selectAllFiltered(): void {
  const filtered = filteredApprovals.value;
  const allSelected =
    filtered.length > 0 && filtered.every((a) => batchSelected.value.has(a.id));

  const next = new Set(batchSelected.value);
  for (const a of filtered) {
    if (allSelected) {
      next.delete(a.id);
    } else {
      next.add(a.id);
    }
  }
  batchSelected.value = next;
}

// ---- WebSocket real-time updates ----

let wsCleanup: (() => void) | null = null;

export function subscribeToWS(): () => void {
  if (wsCleanup) wsCleanup();

  const refreshVisible = () => {
    if (document.visibilityState === 'visible' && approvalView.value === 'pending') void loadApprovals();
  };
  const unsubscribe = onMessage((msg: WSMessage) => {
    if (msg.type === 'connected') { refreshVisible(); return; }
    if (msg.type === 'new_approval') {
      const newApproval = (msg as unknown as WSNewApproval).approval;
      if (!newApproval) { refreshVisible(); return; }
      const newKey = String(newApproval.id);
      if (inFlightSends.has(newKey) || settledSends.has(newKey) || staleSends.has(newKey)) return;
      if (approvals.value.some(approval => String(approval.id) === String(newApproval.id))) return;
      if (approvalView.value === 'pending') {
        approvals.value = [newApproval, ...approvals.value];
        _pendingCountCache.value = _pendingCountCache.value + 1;
      }
      tabBarPendingCount.value = tabBarPendingCount.value + 1;
    } else if (msg.type === 'approval_update') {
      const updated = (msg as unknown as WSApprovalUpdate).approval;
      // The legacy gateway signal has no payload. Re-read the queue instead of
      // dereferencing undefined and leaving externally rewritten drafts stale.
      if (!updated) {
        void loadApprovals();
        return;
      }
      // An optimistic approve already removed this row and already decremented the
      // counts. Letting the echo of its own decision run would decrement them twice.
      if (inFlightSends.has(String(updated.id))) return;
      const wasPending = approvals.value.some(approval => String(approval.id) === String(updated.id)
        && approval.status === 'pending');
      approvals.value = approvals.value.map((a) =>
        String(a.id) === String(updated.id) ? updated : a,
      );
      if (approvalView.value === 'pending' && updated.status !== 'pending') {
        approvals.value = approvals.value.filter(
          (a) => String(a.id) !== String(updated.id),
        );
        if (wasPending && _pendingCountCache.value > 0) {
          _pendingCountCache.value = _pendingCountCache.value - 1;
        }
        if (wasPending && tabBarPendingCount.value > 0) {
          tabBarPendingCount.value = tabBarPendingCount.value - 1;
        }
      }
    }
  });
  const timer = setInterval(refreshVisible, 30_000);
  document.addEventListener('visibilitychange', refreshVisible);
  window.addEventListener('online', refreshVisible);
  window.addEventListener('focus', refreshVisible);
  const cleanup = () => {
    unsubscribe();
    clearInterval(timer);
    document.removeEventListener('visibilitychange', refreshVisible);
    window.removeEventListener('online', refreshVisible);
    window.removeEventListener('focus', refreshVisible);
  };
  wsCleanup = cleanup;
  return () => {
    if (wsCleanup === cleanup) {
      cleanup();
      wsCleanup = null;
    }
  };
}

// ---- Utility ----

export function getAgeClass(dateStr: string): string {
  const hours = (Date.now() - new Date(dateStr).getTime()) / 3600000;
  if (hours < 1) return 'ageFresh';
  if (hours < 4) return 'ageWarm';
  return 'ageStale';
}

export function getStatusInfo(status: string): { label: string; cls: string } {
  switch (status) {
    case 'sent':
    case 'executed':
      return { label: 'Sent', cls: 'statusSent' };
    case 'approved':
      return { label: 'Approved', cls: 'statusSent' };
    case 'rejected':
      return { label: 'Declined', cls: 'statusSkipped' };
    case 'expired':
      return { label: 'Expired', cls: 'statusExpired' };
    case 'send_failed':
      return { label: 'Failed', cls: 'statusRejected' };
    case 'blocked':
      return { label: 'Blocked', cls: 'statusRejected' };
    default:
      return { label: status, cls: '' };
  }
}

/**
 * The person the draft is going to. A card is linked to the contact it is ABOUT (often the
 * client), but a draft on a client's card can go to the listing agent, lender, or title rep;
 * the card must name that recipient, never the client (Joseph 2026-09-24).
 */
export function extractContactName(approval: Approval): string {
  let name = rawContactName(approval);
  // An unnamed group reads as its first person, never a run of numbers.
  if (/[,;]/.test(name) && isGroupText(approval)) name = groupRecipients(approval)[0] || name;
  // A person with no saved name reads as a phone number, never a raw +1 string (PH-06b).
  return /^[\d\s+().-]{10,}$/.test(name) ? formatRecipient(name) : name;
}

function rawContactName(approval: Approval): string {
  if (approval.recipient_is_linked === false) {
    if (approval.recipient_name) return approval.recipient_name;
    const to = approval.recipient_address || (approval.draft_content as Record<string, string> | null)?.to;
    if (to) return formatRecipient(to);
  }
  if (approval.contact_name) return approval.contact_name;
  if (approval.title) {
    const match = approval.title.match(/(?:SMS|Email|Reply|Text) to ([^\u2014\-]+)/i);
    if (match) return match[1].trim();
  }
  const draft = approval.draft_content as Record<string, string> | null;
  if (draft?.to) return draft.to;
  return 'Unknown';
}

/** The linked contact a draft is about, when the draft goes to someone else. */
export function extractRegardingName(approval: Approval): string | null {
  if (approval.recipient_is_linked !== false) return null;
  const name = approval.regarding_name;
  const contact = extractContactName(approval);
  return name && name !== contact && formatRecipient(name) !== contact ? name : null;
}

/** The single phone number an SMS draft goes to, or null for group or missing recipients. */
export function singleSmsRecipient(approval: Approval): string | null {
  const to = String((approval.draft_content as Record<string, unknown> | null)?.to ?? '').trim();
  if (!to || /[,;@]/.test(to)) return null;
  return to.replace(/\D/g, '').length >= 10 ? to : null;
}

export function formatRecipient(val: string): string {
  if (!val) return '';
  const digits = val.replace(/\D/g, '');
  if (digits.length === 11 && digits[0] === '1') {
    return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  if (val.length > 30) return val.substring(0, 28) + '...';
  return val;
}
